// Le date delle storie e delle foto, messe in parole. Nel database sono EDTF: «1954», «1954-06»,
// «1954-06-12», con «~» (circa), «?» (incerta) o «%» (tutte e due), e gli intervalli «1950/1959»,
// «1950/..». EDTF ammette anche cifre NON SPECIFICATE: «195X» sono gli anni Cinquanta, «19XX» il
// Novecento, «1950-XX» il 1950 senza il mese. La rilettura delle date (Claude) ne scrive; e il
// 30 settembre a mamma è arrivato «195X» scritto così, nelle storie degli anni (richiesta #22).
//
// Da qui passa ogni data che mamma, un ospite, un'email o il libro mettono davanti a qualcuno.
// Puro, senza DOM né database: lo usano il client e il server. I dati salvati non cambiano mai.

const MESI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];
const DECINE = ['', 'Dieci', 'Venti', 'Trenta', 'Quaranta', 'Cinquanta', 'Sessanta', 'Settanta', 'Ottanta', 'Novanta'];

type Articolo = 'il' | "l'" | 'i' | 'gli';
/** I secoli che in italiano hanno un nome, con il loro articolo («l'Ottocento»). */
const SECOLI: Record<number, [Articolo, string]> = {
  12: ['il', 'Duecento'], 13: ['il', 'Trecento'], 14: ['il', 'Quattrocento'], 15: ['il', 'Cinquecento'],
  16: ['il', 'Seicento'], 17: ['il', 'Settecento'], 18: ["l'", 'Ottocento'], 19: ['il', 'Novecento'], 20: ['il', 'Duemila'],
};
const ARTICOLATE: Record<'in' | 'da' | 'a', Record<Articolo, string>> = {
  in: { il: 'nel', "l'": "nell'", i: 'nei', gli: 'negli' },
  da: { il: 'dal', "l'": "dall'", i: 'dai', gli: 'dagli' },
  a: { il: 'al', "l'": "all'", i: 'ai', gli: 'agli' },
};
const davanti = (parola: string, nome: string): string => (parola.endsWith("'") ? parola + nome : `${parola} ${nome}`);

type Qualificatore = '' | '?' | '~' | '%';

/** Un momento già in parole, con quel che serve per metterlo dentro una frase. */
type Punto = {
  nome: string;          // «12 giugno 1954», «1954», «anni Cinquanta», «Novecento»
  articolo: Articolo;    // quello che vuole davanti: «il 1954», «gli anni Cinquanta», «l'Ottocento»
  articoloSempre: boolean; // anche da solo: «il Novecento» sì, «anni Cinquanta» no
  giorno: boolean;       // un giorno si dice «il 12 giugno», mai «nel 12 giugno»
  da: number;            // il primo anno che copre: per ordinare e raggruppare
  fino: number;          // l'ultimo: 1959 per «195X», 1954 per «1954-06»
  q: Qualificatore;
};

/** Un decennio, un secolo o un millennio di cui si sa solo l'inizio (`ignote` = cifre X). */
function periodo(da: number, ignote: number, q: Qualificatore): Punto {
  const fino = da + 10 ** ignote - 1;
  const anni = (nome: string, articolo: Articolo = 'gli'): Punto => ({ nome, articolo, articoloSempre: false, giorno: false, da, fino, q });
  const secolo = SECOLI[Math.floor(da / 100)];
  if (ignote === 2 && secolo) return { nome: secolo[1], articolo: secolo[0], articoloSempre: true, giorno: false, da, fino, q };
  if (ignote !== 1 || !secolo) return anni(`anni ${da}–${fino}`); // nessun nome che suoni bene
  const decina = Math.floor((da % 100) / 10);
  const delSecolo = davanti(secolo[0] === 'il' ? 'del' : "dell'", secolo[1]);
  if (decina === 0) return anni(`primi anni ${delSecolo}`, 'i');
  // Il Novecento si sottintende: sono gli anni di mamma. Gli altri secoli si dicono.
  return anni(secolo[1] === 'Novecento' ? `anni ${DECINE[decina]}` : `anni ${DECINE[decina]} ${delSecolo}`);
}

const UN_PUNTO = /^(\d[\dXxu]{3})(?:-(\d{2}|[Xxu]{2})(?:-(\d{2}|[Xxu]{2}))?)?([?~%])?(?:[ T].*)?$/;

/** Un solo momento EDTF, o null se non lo sappiamo leggere (mese 13, 31 febbraio, stagioni). */
function leggiPunto(testo: string): Punto | null {
  const m = UN_PUNTO.exec(testo);
  if (!m) return null;
  const anno = m[1]!.replace(/[xu]/g, 'X');
  if (!/^\d+X*$/.test(anno)) return null; // «1X5X» non è una data
  const q = (m[4] ?? '') as Qualificatore;
  const ignote = anno.length - anno.replace(/X+$/, '').length;
  const da = Number(anno.replace(/X/g, '0'));
  if (ignote > 0) return periodo(da, ignote, q); // di un decennio non si dicono mese e giorno
  const mese = m[2] && /^\d+$/.test(m[2]) ? Number(m[2]) : null;
  if (mese !== null && (mese < 1 || mese > 12)) return null; // anche le stagioni EDTF (21–24): non esistono
  const giorno = mese !== null && m[3] && /^\d+$/.test(m[3]) ? Number(m[3]) : null;
  if (giorno !== null && (giorno < 1 || giorno > new Date(Date.UTC(da, mese!, 0)).getUTCDate())) return null;
  const nome = giorno !== null ? `${giorno} ${MESI[mese! - 1]} ${da}` : mese !== null ? `${MESI[mese - 1]} ${da}` : String(da);
  return { nome, articolo: 'il', articoloSempre: false, giorno: giorno !== null, da, fino: da, q };
}

type Data =
  | { tipo: 'punto'; punto: Punto }
  | { tipo: 'intervallo'; da: Punto | null; a: Punto | null }; // null = aperto («..»)

function leggi(edtf: string): Data | null {
  const testo = edtf.trim();
  const lati = testo.split('/');
  if (lati.length === 1) {
    const punto = leggiPunto(testo);
    return punto ? { tipo: 'punto', punto } : null;
  }
  if (lati.length !== 2) return null;
  const lato = (s: string): Punto | null | undefined => (s === '..' || s === '' ? null : leggiPunto(s) ?? undefined);
  const da = lato(lati[0]!), a = lato(lati[1]!);
  if (da === undefined || a === undefined || (da === null && a === null)) return null;
  return { tipo: 'intervallo', da, a };
}

/** Come si dice l'incertezza: dopo («1954 circa», come la cronologia da sempre) o prima
 *  («intorno al 1954», come le etichette del quaderno). */
export type Incertezza = 'dopo' | 'prima';

const coda = (q: Qualificatore): string => (q === '?' ? ' (data incerta)' : q ? ' circa' : '');
const colArticolo = (p: Punto): string => davanti(p.articolo, p.nome);
const con = (prep: 'in' | 'da' | 'a', p: Punto): string =>
  prep === 'in' && p.giorno ? `il ${p.nome}` : davanti(ARTICOLATE[prep][p.articolo], p.nome);

function etichetta(p: Punto, incertezza: Incertezza): string {
  if (incertezza === 'prima' && p.q) {
    if (p.q === '~') return `intorno ${con('a', p)}`;
    if (p.q === '?') return `forse ${con('in', p)}`;
    return `forse intorno ${con('a', p)}`;
  }
  return (p.articoloSempre ? colArticolo(p) : p.nome) + coda(p.q);
}

/**
 * La data da sola, come titoletto: «12 giugno 1954», «giugno 1954 circa», «anni Cinquanta»,
 * «il Novecento», «1964 – 1966», «dagli anni Cinquanta». Null se non la sappiamo leggere: il
 * chiamante decide se mostrare la stringa com'è o tacere.
 */
export function dataLeggibile(edtf: string, opts: { incertezza?: Incertezza } = {}): string | null {
  const d = leggi(edtf);
  if (!d) return null;
  const inc = opts.incertezza ?? 'dopo';
  if (d.tipo === 'punto') return etichetta(d.punto, inc);
  if (d.da && d.a) return `${etichetta(d.da, inc)} – ${etichetta(d.a, inc)}`;
  if (d.da) return con('da', d.da) + coda(d.da.q);
  return `fino ${con('a', d.a!)}${coda(d.a!.q)}`;
}

/**
 * La data dentro una frase: «nel 1954», «nel giugno 1954», «il 12 giugno 1954», «negli anni
 * Cinquanta», «nel Novecento», «tra il 1964 e il 1966». Null se non la sappiamo leggere.
 */
export function nellaData(edtf: string): string | null {
  const d = leggi(edtf);
  if (!d) return null;
  if (d.tipo === 'punto') return con('in', d.punto) + coda(d.punto.q);
  if (d.da && d.a) return `tra ${colArticolo(d.da)}${coda(d.da.q)} e ${colArticolo(d.a)}${coda(d.a.q)}`;
  return dataLeggibile(edtf);
}

/** Il primo anno che la data copre: 1950 per «195X», 1900 per «19XX». Per ordinare e per
 *  raggruppare per decennio: `parseInt('195X')` dà 195, cioè «gli anni 190». */
export function annoIniziale(edtf: string | null | undefined): number | null {
  if (!edtf) return null;
  const d = leggi(edtf);
  if (d) return d.tipo === 'punto' ? d.punto.da : (d.da ?? d.a)!.da;
  // Una data che non sappiamo dire (una stagione, «1964-22») ha comunque il suo anno, come prima.
  const anno = /^\s*(\d[\dXxu]{3})/.exec(edtf)?.[1];
  return anno && /^\d+[Xxu]*$/.test(anno) ? Number(anno.replace(/[Xxu]/g, '0')) : null;
}

/** Il primo e l'ultimo anno che la data copre: «195X» → 1950–1959, «19XX» → 1900–1999,
 *  «1964/1966» → 1964–1966, «1950/..» → 1950 e null. Null se non la sappiamo leggere.
 *  Servono a riempire `event_year_min/max`, su cui album, capitoli e libro ordinano e raggruppano. */
export function anniDellaData(edtf: string | null | undefined): { min: number | null; max: number | null } | null {
  const d = edtf ? leggi(edtf) : null;
  if (!d) return null;
  if (d.tipo === 'punto') return { min: d.punto.da, max: d.punto.fino };
  return { min: d.da?.da ?? null, max: d.a?.fino ?? null };
}

/** Vero se dell'anno si sa solo il decennio o il secolo («195X», «19XX»): lì un anno solo
 *  («1950») direbbe una cosa più precisa di quella che lei ha raccontato. */
export function annoIncompleto(edtf: string | null | undefined): boolean {
  return !!edtf && /(?:^|\/)\d{1,3}[Xxu]/.test(edtf.trim()) && leggi(edtf) !== null;
}

/** L'anno in breve, sotto un nome: «1952», o «anni Venti» per «192X-05-10». Null se un anno non
 *  c'è («--05-10»): meglio tacere che mostrarle il codice, che è come le era arrivato «195X». */
export function annoInBreve(edtf: string | null | undefined): string | null {
  if (!edtf) return null;
  const intero = /^\s*(\d{4})(?![\dXxu])/.exec(edtf)?.[1];
  if (intero) return intero;
  const d = leggi(edtf);
  const p = d ? (d.tipo === 'punto' ? d.punto : d.da ?? d.a) : null;
  return p ? (p.articoloSempre ? colArticolo(p) : p.nome) : null;
}

/** Per ordinare due date EDTF come stringhe: «195X» va con il 1950, non dopo il 1959. */
export function chiaveData(edtf: string): string {
  return edtf.replace(/[Xxu]/g, '0');
}
